import React, {useEffect, useRef, useState} from 'react';
import {Pressable, useWindowDimensions, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import ChessboardComponent from 'chessboard-package';

// DEVIATION from the plan: relative imports instead of the '@' alias — see
// CourseVideoPlayer.tsx for why. This file is also transitively imported by
// Task 6's index.test.tsx.
import CCText from '../../../Atoms/CCText';
import {CoursePage} from '../../../Hooks/LmsCourseApi/types';

import {
  applyStudentMove,
  expectedMoveForPly,
  firstMoveSquare,
  legalDestinations,
  positionsAlongLine,
} from '../courseLogic';
import {formatTimestamp, squareOffset} from '../courseFormat';
import {CARD_SHADOW, contentWidth, F, MOBILE_BREAKPOINT} from '../courseTheme';

import CourseButton, {TurnBadge} from './CourseButton';

/** Horizontal padding CourseChapterScreen puts around the chapter content. */
const PAGE_PADDING = 24;

type Props = {
  page: CoursePage;
  onSolved: () => void;
  width?: number;
  /** Jump the player back to where this position came up in the recording. */
  onReplayMoment?: (seconds: number) => void;
  /** The course this puzzle belongs to, so the tutor can be asked about it. */
  bookUuid?: string;
  /**
   * The coach's current demonstration step, if it is playing one. While this
   * is set the board shows the step's position and ignores input.
   */
  coachStep?: {
    fen: string;
    highlight: string[];
    san: string | null;
    arrows?: string[][];
  } | null;
  /**
   * Hand a hint to the coach instead of showing it here, so every explanation
   * on this screen comes from one place. `square` is set on the second press,
   * the one that points at the board.
   */
  onHint?: (hint: {text: string; square: string | null; level: number}) => void;
};

/** How long the opponent appears to think before replying. */
const REPLY_DELAY_MS = 700;

/** How long a wrong move stays on the board before it is taken back. */
const WRONG_REVERT_MS = 700;

/** Wrong tries after which the solution is offered. */
const REVEAL_AFTER_ATTEMPTS = 4;

/**
 * A `single_variant` puzzle: the student plays the odd plies, the scripted
 * engine reply lands automatically, and the final odd ply completes the page.
 *
 * The board is `chessboard-package`, the same one the LMS chapter player uses,
 * so a generated course plays exactly like a hand-authored one. That package
 * makes the move itself and then reports it through `handleNewFen` — there is
 * no snapback and no way to veto a drop. A wrong move is therefore undone by
 * remounting the board at the unchanged FEN, keyed on the attempt count, which
 * is the same trick the LMS player uses.
 */
export default function CoursePuzzleBoard({
  page,
  onSolved,
  width = 520,
  onReplayMoment,
  bookUuid,
  coachStep = null,
  onHint,
}: Props) {
  const {width: windowWidth} = useWindowDimensions();

  const [fen, setFen] = useState(page.content);
  // The position before `fen`. The board derives its last-move highlight by
  // diffing the two, which is how chessboard-package is told what just moved.
  const [lastMoveFen, setLastMoveFen] = useState(page.content);
  const [ply, setPly] = useState(1);
  const [solved, setSolved] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [attempts, setAttempts] = useState(0);
  // The moves actually tried, not just how many — the tutor answers about
  // the student's own move, which needs the move itself.
  const [wrongMoves, setWrongMoves] = useState<string[]>([]);
  const [history, setHistory] = useState<{ply: number; san: string; side: 'you' | 'opponent'}[]>([]);
  const [thinking, setThinking] = useState(false);
  // 0 = not asked, 1 = the written hint, 2 = the written hint plus the square.
  const [hintLevel, setHintLevel] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  // Solved by being shown the answer rather than by finding it.
  const [revealed, setRevealed] = useState(false);
  // A tutor answer being shown on the board. The puzzle is paused while this
  // is set, and untouched by it — stepping through an explanation must not
  // count as playing moves.
  const [preview, setPreview] = useState<{
    positions: string[];
    index: number;
    arrows: [string, string][];
    highlight: string[];
    lineSan: string[];
  } | null>(null);

  const replyTimer = useRef<ReturnType<typeof setTimeout>>();

  const clearReply = () => {
    if (replyTimer.current != null) {
      clearTimeout(replyTimer.current);
      replyTimer.current = undefined;
    }
  };

  useEffect(() => {
    clearReply();
    setFen(page.content);
    setLastMoveFen(page.content);
    setPly(1);
    setSolved(false);
    setFeedback('');
    setAttempts(0);
    setWrongMoves([]);
    setHistory([]);
    setThinking(false);
    setHintLevel(0);
    setSelected(null);
    setRevealed(false);
    setPreview(null);
  }, [page.id, page.content]);

  // A reply scheduled for a puzzle the student has already left must never
  // land on the next one.
  useEffect(() => clearReply, []);

  const orientation = (page.content.split(' ')[1] ?? 'w') === 'w' ? 'white' : 'black';

  const handleNewFen = (moveData: {from: string; to: string}) => {
    // While the opponent's reply is pending the board is the opponent's, not
    // the student's — accepting a move here would let them play both sides.
    if (solved || thinking || !moveData) return;
    const {from, to} = moveData;
    // Picking a piece up and putting it back is not an attempt at the puzzle;
    // treating it as a wrong move scolds the student for doing nothing.
    if (!from || !to || from === to) return;

    // Judge from the live position. `applyStudentMove` does not replay the
    // line from the start — it applies one student move plus the scripted
    // reply to the FEN it is given and returns the result, so the state has to
    // be threaded through. Passing `page.content` here would judge every move
    // after the first against a stale board.
    const outcome = applyStudentMove(fen, page.moves, ply, from, to);

    if (outcome.status === 'wrong') {
      setFeedback('Not the move — try again.');
      setWrongMoves(m => (m.includes(`${from}${to}`) ? m : [...m, `${from}${to}`]));
      // The package has already put the piece down, so let the student see the
      // move they actually played before taking it back. Remounting
      // immediately makes the piece appear to never have moved, which reads as
      // the board ignoring them rather than rejecting the move.
      setThinking(true);
      clearReply();
      replyTimer.current = setTimeout(() => {
        // Bumping this remounts the board at the unchanged FEN, which is what
        // puts the piece back.
        setAttempts(n => n + 1);
        setThinking(false);
      }, WRONG_REVERT_MS);
      return;
    }

    if (outcome.status === 'reply') {
      // Show the student's move on its own first, then land the reply a beat
      // later. Applying both at once reads as the opponent moving at the same
      // instant, and the student never sees what they just played.
      setLastMoveFen(fen);
      setFen(outcome.afterStudentFen);
      setFeedback('');
      setThinking(true);
      setHistory(h => [...h, {ply, san: outcome.studentSan, side: 'you'}]);

      clearReply();
      replyTimer.current = setTimeout(() => {
        setLastMoveFen(outcome.afterStudentFen);
        setFen(outcome.fen);
        setPly(outcome.nextPly);
        setThinking(false);
        setHistory(h => [
          ...h,
          {ply: ply + 1, san: outcome.replySan, side: 'opponent'},
        ]);
      }, REPLY_DELAY_MS);
      return;
    }

    setLastMoveFen(fen);
    setFen(outcome.fen);
    setSolved(true);
    setFeedback('');
    setHistory(h => [...h, {ply, san: outcome.studentSan, side: 'you'}]);
    onSolved();
  };

  const config = page.config || {};
  const hintText = (config.hint || '').trim();
  // Courses generated before hints existed carry no hint_square, but the square
  // is just the origin of the scripted first move — which is already here. So
  // the "show me where" hint works on every course, old or new.
  const hintSquare = (config.hint_square || firstMoveSquare(page.moves) || '').trim();
  const moment = config.source_timestamp ?? page.timestamp;
  // A hint is only worth offering if there is something to reveal.
  const canHint = !solved && (!!hintText || !!hintSquare);
  const showSquare = hintLevel >= 2;
  // After four wrong tries, or once the hints are spent, the student has done
  // their part — anything beyond that is the puzzle's fault, not theirs.
  const canReveal =
    (page.moves ?? []).length > 0 && (attempts >= REVEAL_AFTER_ATTEMPTS || showSquare);
  const showCard = (hintLevel >= 1 && !!hintText) || (showSquare && !!hintSquare);
  // With no written hint there is no first step to take: pressing once should
  // reveal the square rather than opening a hint card with nothing in it.
  const nextHintLevel = hintText ? hintLevel + 1 : 2;
  // Once the hint is spent it lives in the coach, so "Hint shown" would be a
  // lie whenever that panel is shut — the student would be looking at a dead
  // button and no hint. Pressing again re-opens the coach on the entry that is
  // already there rather than adding another.
  const hintLabel = showSquare
    ? 'See the hint'
    : hintLevel === 0 && hintText
    ? 'Hint'
    : 'Show me where';

  const destinations = selected ? legalDestinations(fen, selected) : [];

  /**
   * Click-to-move, owned here rather than left to chessboard-package.
   *
   * The package has its own click handling, but it keeps the selection in
   * props its wrapper is meant to supply, and in this app that state never
   * updates — clicking a piece highlighted nothing, so the board read as dead
   * even though the move itself landed. This prop is spread onto the inner
   * board last, so it replaces the package's handler. Dragging is untouched.
   */
  const handleSquareClick = (square: string) => {
    if (solved || thinking) return;

    if (selected && square !== selected) {
      if (destinations.includes(square)) {
        setSelected(null);
        handleNewFen({from: selected, to: square});
        return;
      }
      // Clicking another of your own pieces picks that one up instead of
      // reading as a failed move.
      if (legalDestinations(fen, square).length) {
        setSelected(square);
        return;
      }
      setSelected(null);
      return;
    }

    // Clicking the selected piece again puts it back down.
    if (selected === square) {
      setSelected(null);
      return;
    }
    setSelected(legalDestinations(fen, square).length ? square : null);
  };

  // Sized against the content area, not the window: the app's left nav takes
  // ~160px, so a 700px window leaves ~540px of content and `windowWidth * 0.42`
  // left the lesson card under 180px wide. The stacked board is clamped to the
  // parent's padding too, or below ~400px it overflows the page and gives the
  // document a horizontal scrollbar.
  /**
   * Play the one move the student is stuck on, and hand the board back.
   *
   * These puzzles are machine-generated from positions detected on screen, and
   * only the exact stored move is accepted — so an equally-winning alternative,
   * a transposition, or a detector mistake all loop "Not the move" forever with
   * no way out. A wrong puzzle is expected here, not exceptional, so there has
   * to be a door.
   *
   * It reveals a single move rather than the whole line: a puzzle is a
   * sequence, and playing it all out ends the exercise, where showing the move
   * in front of them leaves the rest still to be found. The move is fed through
   * the normal path, so the reply still lands a beat later and the last move
   * still completes the page.
   */
  const revealNextMove = () => {
    if (solved || thinking) return;
    const scripted = expectedMoveForPly(page.moves, ply);
    const uci = (scripted?.move ?? '').trim().toLowerCase();
    if (!/^[a-h][1-8][a-h][1-8]/.test(uci)) return;

    setRevealed(true);
    setSelected(null);
    handleNewFen({from: uci.slice(0, 2), to: uci.slice(2, 4)});
  };

  const available = contentWidth(windowWidth);
  const stacked = available < MOBILE_BREAKPOINT;
  const boardSize = stacked
    ? Math.min(available - PAGE_PADDING * 2, 520)
    : Math.min(width, available * 0.42);

  const hintMarker =
    showSquare && hintSquare ? squareOffset(hintSquare, boardSize, orientation) : null;
  const selectedMarker =
    selected && !preview ? squareOffset(selected, boardSize, orientation) : null;
  // Squares the tutor pointed at, drawn the same way the hint marker is — the
  // package's own square styling must not be replaced (see the hint marker).
  const tutorMarkers = (preview?.highlight ?? [])
    .map(sq => ({sq, at: squareOffset(sq, boardSize, orientation)}))
    .filter(m => m.at);

  // The coach is demonstrating: the board shows its step rather than the
  // student's position, and their moves are ignored until it stops. Letting
  // them play into a demonstration would leave the two out of step with no
  // way back to either.
  const coachMarkers = (coachStep?.highlight ?? [])
    .map(sq => ({sq, at: squareOffset(sq, boardSize, orientation)}))
    .filter(m => m.at);

  // The position shown by the coach's *previous* step. The package derives its
  // last-move highlight by diffing the two, so without this each demonstrated
  // move lands with no indication of what just moved.
  const coachPrevRef = useRef<string | null>(null);
  const coachPrevFen = coachPrevRef.current;
  useEffect(() => {
    coachPrevRef.current = coachStep ? coachStep.fen : null;
  }, [coachStep]);

  const showAnswer = (answer: {
    line: string[];
    arrows: [string, string][];
    highlight: string[];
    line_san: string[];
  } | null) => {
    if (!answer) {
      setPreview(null);
      return;
    }
    // Replayed from the position the student is actually looking at. The
    // server validated the line, so this should not truncate — but if it does,
    // the student sees a short line rather than a wrong board.
    const positions = positionsAlongLine(fen, answer.line);
    if (!positions.length) return;
    setSelected(null);
    setPreview({
      positions,
      index: 0,
      arrows: answer.arrows ?? [],
      highlight: answer.highlight ?? [],
      lineSan: answer.line_san ?? [],
    });
  };
  const destinationMarkers = destinations
    .map(sq => ({sq, at: squareOffset(sq, boardSize, orientation)}))
    .filter(d => d.at);

  return (
    <View
      style={{
        flexDirection: stacked ? 'column' : 'row',
        gap: stacked ? 16 : 24,
        alignItems: stacked ? 'center' : 'stretch',
      }}>
      <View style={{alignItems: 'center'}}>
        <View style={{width: boardSize, borderRadius: 4, overflow: 'hidden'}}>
          {/* The hint marker is an overlay rather than a `customSquareStyles`
              prop: that prop is spread last onto the inner board, so it
              replaces the package's own square styling — including the
              selection highlight, which leaves clicking a piece looking like
              nothing happened. */}
          {/* Selection and legal-move feedback, drawn here for the same reason
              as the hint marker. */}
          {tutorMarkers.map(({sq, at}) => (
            <View
              key={`tutor-${sq}`}
              pointerEvents="none"
              style={{
                position: 'absolute',
                zIndex: 2,
                left: at!.left,
                top: at!.top,
                width: at!.size,
                height: at!.size,
                borderWidth: 3,
                borderColor: F.teal,
                borderRadius: 4,
              }}
            />
          ))}
          {/* What the coach is pointing at as it speaks. Orange rather than
              the tutor's teal, so it is obvious which assistant is talking. */}
          {coachMarkers.map(({sq, at}) => (
            <View
              key={`coach-${sq}`}
              pointerEvents="none"
              style={{
                position: 'absolute',
                zIndex: 3,
                left: at!.left,
                top: at!.top,
                width: at!.size,
                height: at!.size,
                borderWidth: 3,
                borderColor: F.hintFg,
                backgroundColor: F.hintBg,
                borderRadius: 4,
              }}
            />
          ))}
          {/* While the coach demonstrates, the student's moves are swallowed
              here rather than disabled on the package — a board that stops
              responding mid-sentence reads as broken, and letting them play
              into a demonstration desyncs the two with no way back. */}
          {coachStep ? (
            <View
              style={{
                position: 'absolute',
                zIndex: 4,
                left: 0,
                top: 0,
                width: boardSize,
                height: boardSize,
              }}
            />
          ) : null}

          {selectedMarker ? (
            <View
              pointerEvents="none"
              style={{
                position: 'absolute',
                zIndex: 2,
                left: selectedMarker.left,
                top: selectedMarker.top,
                width: selectedMarker.size,
                height: selectedMarker.size,
                backgroundColor: 'rgba(61,171,158,0.45)',
              }}
            />
          ) : null}

          {destinationMarkers.map(({sq, at}) => (
            <View
              key={`dest-${sq}`}
              pointerEvents="none"
              style={{
                position: 'absolute',
                zIndex: 2,
                left: at!.left,
                top: at!.top,
                width: at!.size,
                height: at!.size,
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <View
                style={{
                  width: at!.size * 0.3,
                  height: at!.size * 0.3,
                  borderRadius: at!.size * 0.15,
                  backgroundColor: 'rgba(61,171,158,0.7)',
                }}
              />
            </View>
          ))}

          {hintMarker ? (
            <View
              pointerEvents="none"
              style={{
                position: 'absolute',
                zIndex: 2,
                left: hintMarker.left,
                top: hintMarker.top,
                width: hintMarker.size,
                height: hintMarker.size,
                borderWidth: 4,
                borderColor: F.hintFg,
                borderRadius: 4,
              }}
            />
          ) : null}
          <ChessboardComponent
            key={`course-board-${page.id}-wrong${attempts}`}
            position={
              coachStep
                ? coachStep.fen
                : preview
                ? preview.positions[preview.index]
                : fen
            }
            // The package diffs these two to work out what just moved and
            // paints the from/to squares — that is the last-move highlight.
            lastMoveFen={
              coachStep
                ? coachPrevFen ?? coachStep.fen
                : preview
                ? preview.positions[Math.max(0, preview.index - 1)]
                : lastMoveFen
            }
            customArrows={
              coachStep
                ? (coachStep as any).arrows
                : preview
                ? preview.arrows
                : undefined
            }
            boardOrientation={orientation}
            boardWidth={boardSize}
            disabled={solved || thinking || !!preview || !!coachStep}
            handleNewFen={handleNewFen}
            onSquareClick={handleSquareClick}
          />
        </View>
      </View>

      <View
        style={[
          {
            flex: stacked ? undefined : 1,
            width: stacked ? boardSize : undefined,
            backgroundColor: F.white,
            borderRadius: F.radius.card,
            overflow: 'hidden',
          },
          CARD_SHADOW,
        ]}>
        <View style={{padding: stacked ? 16 : 32, gap: 16, flex: 1}}>
          <View style={{gap: 10}}>
            {/* The moment in the recording is metadata, not a title. It used
                to be the title, which is why every puzzle was called
                "Puzzle at 27:50". */}
            {moment != null ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Replay this moment"
                disabled={!onReplayMoment}
                onPress={() => onReplayMoment?.(moment)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  alignSelf: 'flex-start',
                  gap: 6,
                  backgroundColor: F.badgeBg,
                  borderRadius: 999,
                  paddingHorizontal: 10,
                  paddingVertical: 5,
                }}>
                <Ionicons name="play-circle" size={13} color={F.mutedText} />
                <CCText
                  style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: F.brown,
                    letterSpacing: 0.2,
                  }}>
                  {`From ${formatTimestamp(moment)}`}
                </CCText>
              </Pressable>
            ) : null}

            {/* Which side you are playing is a precondition, not a footnote.
                It used to sit under the move list and hint card, which on a
                stacked phone put it below the fold entirely — students picked
                up the wrong colour and got "Not the move" with no idea why. */}
            <TurnBadge turn={orientation === 'white' ? 'w' : 'b'} />

            <CCText
              style={{
                // The LMS player drops the puzzle title to 18 when stacked;
                // desktop type on a 360px phone wraps a generated title to
                // four lines.
                fontSize: stacked ? 18 : 24,
                fontWeight: '600',
                color: F.brownDark,
                lineHeight: stacked ? 25 : 32,
              }}>
              {page.heading || 'Solve the position'}
            </CCText>
          </View>

          {page.text ? (
            <CCText
              style={{
                fontSize: 16,
                fontWeight: '500',
                color: F.brown,
                lineHeight: 28,
              }}>
              {page.text}
            </CCText>
          ) : null}

          {/* Only the moves already played. Listing the whole scripted line
              here would hand the student the solution. */}
          {history.length ? (
            <View style={{gap: 6}}>
              <CCText style={{fontSize: 12, fontWeight: '600', color: F.mutedText}}>
                MOVES
              </CCText>
              <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 6}}>
                {history.map((entry, i) => {
                  const latest = i === history.length - 1;
                  return (
                    <View
                      key={`${entry.ply}-${entry.san}`}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 6,
                        borderRadius: 6,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        backgroundColor: latest ? F.teal : F.badgeBg,
                      }}>
                      <CCText
                        style={{
                          fontSize: 11,
                          fontWeight: '600',
                          color: latest ? F.white : F.mutedText,
                        }}>
                        {entry.side === 'you' ? 'You' : 'Opponent'}
                      </CCText>
                      <CCText
                        style={{
                          fontSize: 14,
                          fontWeight: '600',
                          color: latest ? F.white : F.brownDark,
                        }}>
                        {entry.san}
                      </CCText>
                    </View>
                  );
                })}
              </View>
              {thinking ? (
                <CCText style={{fontSize: 13, fontWeight: '600', color: F.mutedText}}>
                  Opponent is thinking…
                </CCText>
              ) : null}
            </View>
          ) : null}

          {solved ? (
            <View style={{gap: 12, paddingTop: 8}}>
              <View
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 32,
                  backgroundColor: F.teal,
                  borderBottomWidth: 3.2,
                  borderBottomColor: F.tealDark,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Ionicons name="checkmark" size={32} color={F.white} />
              </View>
              <CCText style={{fontSize: 24, fontWeight: '500', color: F.teal}}>
                {/* Don't congratulate someone as though they found it unaided,
                    but they may well have found the rest of the line
                    themselves after one nudge. */}
                {revealed ? 'Solved with help' : "Nice! It's correct"}
              </CCText>
            </View>
          ) : null}

          {feedback && !solved ? (
            <View
              style={{
                alignSelf: 'flex-start',
                backgroundColor: F.wrongBg,
                borderWidth: 1,
                borderColor: F.wrongFg,
                borderRadius: 4,
                paddingHorizontal: 12,
                paddingVertical: 8,
              }}>
              <CCText style={{fontSize: 14, fontWeight: '600', color: F.wrongFg}}>
                {feedback}
              </CCText>
            </View>
          ) : null}

          {/* While an explanation is on the board, the controls are for the
              explanation — the puzzle is paused underneath it. */}
          {preview ? (
            <View
              style={{
                backgroundColor: 'rgba(61,171,158,0.10)',
                borderWidth: 1,
                borderColor: F.teal,
                borderRadius: 8,
                padding: 12,
                gap: 10,
              }}>
              <CCText style={{fontSize: 13, fontWeight: '600', color: F.tealDark}}>
                {preview.lineSan.length
                  ? `${preview.lineSan.slice(0, preview.index).join(' ') || 'Start'}  ·  move ${preview.index} of ${preview.positions.length - 1}`
                  : 'Shown on the board'}
              </CCText>
              <View style={{flexDirection: 'row', gap: 8, flexWrap: 'wrap'}}>
                <CourseButton
                  label="Back"
                  variant="review"
                  disabled={preview.index === 0}
                  onPress={() =>
                    setPreview(p => (p ? {...p, index: Math.max(0, p.index - 1)} : p))
                  }
                />
                <CourseButton
                  label="Next move"
                  disabled={preview.index >= preview.positions.length - 1}
                  onPress={() =>
                    setPreview(p =>
                      p
                        ? {...p, index: Math.min(p.positions.length - 1, p.index + 1)}
                        : p,
                    )
                  }
                />
                <CourseButton
                  label="Back to my puzzle"
                  variant="review"
                  onPress={() => setPreview(null)}
                />
              </View>
            </View>
          ) : null}

          {/* "Ask about this position" used to sit here. The course coach in
              the rail does everything it did and more — it knows the position,
              and it can demonstrate a line on the board while talking through
              it — so two assistants on one screen was a choice the student had
              no way to make well. The preview machinery above stays: the coach
              drives it. */}

          {/* The hint card used to sit here. It now arrives in the coach —
              one place every explanation on this screen comes from, so a
              student never has to work out which panel answered them. The
              square is still outlined on the board, because that is board
              feedback rather than an explanation. */}

          <View
            style={{
              marginTop: 'auto' as any,
              paddingTop: 16,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}>
            {/* Hidden rather than rendered disabled once there is nothing
                further to reveal: a greyed button labelled "Show me where"
                promises help it cannot give. */}
            <View style={{flexDirection: 'row', gap: 8, flexWrap: 'wrap'}}>
              {canHint && !(hintLevel > 0 && !hintSquare) ? (
                <CourseButton
                  label={hintLabel}
                  variant="review"
                  onPress={() => {
                    setHintLevel(nextHintLevel);
                    // The board still lights the square on the second press;
                    // the words go to the coach, which is where every other
                    // explanation on this screen now comes from. Pressing once
                    // the hint is spent re-opens the coach on the entry that is
                    // already there — `level` tells the screen not to add a
                    // second copy of the same hint.
                    onHint?.({
                      text: hintText,
                      square: showSquare || nextHintLevel >= 2 ? hintSquare || null : null,
                      level: showSquare ? hintLevel : nextHintLevel,
                    });
                  }}
                />
              ) : null}
              {/* Offered once the student has clearly stalled, or as soon as
                  they have exhausted the hints. A generated puzzle can simply
                  be wrong, and without this that is a dead end. */}
              {!solved && canReveal ? (
                <CourseButton
                  label="Show next move"
                  variant="review"
                  disabled={thinking}
                  onPress={revealNextMove}
                />
              ) : null}
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}
